"use client";

import { useSession } from "next-auth/react";

import { LuX } from "react-icons/lu";
import { toast } from "react-toastify";

import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetClose,
} from "~/components/v3/shadcn/Sheet";

import Spinner from "~/components/themed/spinner";
import { Button } from "~/components/v3/themed/Button";

import { Notification } from "./Notification";
import { useNotifications } from "../NotificationProvider";
import { Tabs, TabsList, TabsContent, TabsTrigger } from "../../shadcn/Tabs";
import { useAllNotifications, useUnreadNotifications } from "./queries";
import Link from "next/link";
import { FaGear } from "react-icons/fa6";
import { api } from "~/utils/api";
import { RxEnvelopeOpen } from "react-icons/rx";

type Props = {
    isOpen: boolean;
    setOpen: (show: boolean) => void;
};

export const NotificationSheet = ({ isOpen, setOpen }: Props) => {
    const { data: session } = useSession();
    const user = session?.user;
    const utils = api.useUtils();

    const { markAllNotificationsAsRead } = useNotifications();

    const {
        data: allNotis,
        isLoading: isAllLoading,
        refetch: refetchAllNotis,
    } = useAllNotifications(user?.id ?? "");

    const {
        data: unreadNotis,
        isLoading: isUnreadLoading,
        refetch: refetchUnreadNotis,
    } = useUnreadNotifications(user?.id ?? "");

    const handleMarkAllAsRead = async () => {
        if (!user || !markAllNotificationsAsRead) return;

        const toastId = "mark-all-as-read";
        toast.loading("Marking all notifications as read...", {
            toastId,
        });

        try {
            await markAllNotificationsAsRead().then(() => {
                toast.dismiss(toastId);
                toast.success("All notifications marked as read!", {
                    autoClose: 3000,
                });
            });

            refetchAllNotis();
            refetchUnreadNotis();
            utils.notification.getUnreadNotificationsCount.invalidate();
        } catch (error) {
            toast.dismiss(toastId);
            toast.error(
                "Failed to mark all notifications as read. Please try again.",
            );
        }
    };

    return (
        <Sheet open={isOpen} onOpenChange={() => setOpen(false)}>
            <SheetContent
                showCloseIcon={false}
                className="w-full overflow-y-scroll rounded-br-md rounded-tr-md border-none bg-[#121418] pb-20 pl-2 md:min-w-[450px]"
            >
                <SheetHeader>
                    <div className="flex w-full items-center justify-between px-6 py-4 pb-0">
                        <SheetTitle className="text-lg">
                            Notifications
                        </SheetTitle>

                        <Link
                            href="/preferences"
                            onClick={() => setOpen(false)}
                            className="flex w-full justify-end p-0 px-4"
                        >
                            <FaGear className="h-5 w-5" />
                            <span className="sr-only">Edit preferences</span>
                        </Link>

                        <SheetClose className="focus:ring-ring rounded-sm opacity-90 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
                            <LuX className="h-5 w-5" />
                            <span className="sr-only">Close</span>
                        </SheetClose>
                    </div>

                    <div className="align-end flex w-full px-6">
                        <Button
                            onClick={handleMarkAllAsRead}
                            variant="tertiary"
                            size="sm"
                            className=""
                        >
                            <RxEnvelopeOpen />
                            Mark all as read
                        </Button>
                    </div>
                </SheetHeader>

                <Tabs defaultValue="unread" className="w-full">
                    <TabsList className="flex h-auto w-full justify-start rounded-none border-b-[1px] border-[#363A41] p-0">
                        <TabsTrigger
                            className="rounded-none px-8 py-3 data-[state=active]:border-b-2 data-[state=active]:border-white data-[state=active]:bg-[#121418]"
                            value="unread"
                        >
                            Unread
                        </TabsTrigger>
                        <TabsTrigger
                            className="rounded-none px-8 py-3 data-[state=active]:border-b-2 data-[state=active]:border-white data-[state=active]:bg-[#121418]"
                            value="all"
                        >
                            All
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="all" className="text-base">
                        <div>
                            {isAllLoading ? (
                                <div className="p-4 text-center">
                                    <Spinner type="medium" />
                                </div>
                            ) : allNotis?.length ? (
                                <div className="flex flex-col gap-2">
                                    {allNotis.map((notification) => (
                                        <Notification
                                            key={notification.id}
                                            notification={notification}
                                            user={user}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="p-4 text-center text-v3-N-300">
                                    No unread notifications.
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    <TabsContent value="unread" className="text-base">
                        <div>
                            {isUnreadLoading ? (
                                <div className="p-4 text-center">
                                    <Spinner type="medium" />
                                </div>
                            ) : unreadNotis?.length ? (
                                <div className="flex flex-col gap-2">
                                    {unreadNotis.map((notification) => (
                                        <Notification
                                            key={notification.id}
                                            notification={notification}
                                            user={user}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="p-4 text-center text-v3-N-300">
                                    No unread notifications.
                                </div>
                            )}
                        </div>
                    </TabsContent>
                </Tabs>
            </SheetContent>
        </Sheet>
    );
};
